// The chat as a state of the dock (R1799; cards 15, 16 and 19 of mockup
// round 085 and card 05 b of round 087; ui-map-interface.md §12.0 "The
// chat").
//
// A telephone opens the chat as the chat band, at one fixed height over
// the tab bar. A desktop takes the desktop chat setting (R1838): SMALL and
// LARGE float the chat box at the bottom-left of the map as a unit of the
// console, and DOCKED opens the chat pane at the foot of the dock column,
// at a height its grip sets (R1870). At rest the chat line stands in the dock of a
// telephone and of DOCKED, and inside the closed box of SMALL and LARGE as
// its peek (R2057): the newest line for a few seconds, then the count and
// the channels that hold unread lines.
//
// Pure rules and markup: the overlay reads the settings, the state and the
// clock, and gets answers and strings back, so a test pins every rule with
// no layout engine.

import { ChatChannel } from '../gen/proto/aquaphobia';
import {
  CHAT_BAND_KEYBOARD_LINES, CHAT_BAND_LINES, CHAT_LINE_MS, LAYOUT_CHAT_PANE_COLUMN_CATCH_PX,
  LAYOUT_CHAT_PANE_LINE_CATCH_PX,
} from '../gen/presentation';
import { overlay as S } from '../gen/strings';
import { esc } from './esc';
import { lightCount } from './overlay/thumbdock';
import { CHANNELS } from './overlay/tables';
import { type ChatDesk, type ChatStop, type GameSettings } from './settings';
import { lineIcon } from './lineicon';

// --- the desktop chat setting (R1838) ----------------------------------------

/** The form the chat takes: the band of a telephone, the floating box of
 *  SMALL and LARGE, or the pane of DOCKED. */
export type ChatForm = 'band' | 'box' | 'pane';

/** The form of the chat on this form factor under this setting. A
 *  telephone always takes the band (R1799). */
export function chatForm(sheet: boolean, desk: ChatDesk): ChatForm {
  if (sheet) return 'band';
  return desk === 'docked' ? 'pane' : 'box';
}

/**
 * The three states of the chat box of SMALL and LARGE (R2057, R2369):
 * `open`, `peek` (the closed box rests as one chat line at the
 * bottom-left, the model of the telephone's chat line) and `off` (the
 * CHAT tile turned the chat off, and no box and no line stand).
 */
export type BoxState = 'open' | 'peek' | 'off';

/**
 * The state of the chat box: the setting `chatOn` keeps whether the chat
 * is on at all, and the setting `chat` keeps open or closed. A closed box
 * rests as its peek. A new device stores `chat` false and `chatOn` true,
 * so its first view shows the peek (R2057).
 */
export function boxState(open: boolean, on: boolean): BoxState {
  if (!on) return 'off';
  return open ? 'open' : 'peek';
}

/** The chat keys one press writes. */
export type ChatDeskKeys = Pick<GameSettings, 'chatDesk' | 'chatLarge' | 'chat' | 'chatStop'>;

/**
 * The keys after a pick of the desktop chat setting: the row of SOCIAL.
 * SMALL and LARGE also set the size of the box, and DOCKED keeps the last
 * size for UNDOCK. The pane keeps its stop.
 */
export function pickDesk(s: ChatDeskKeys, desk: ChatDesk): ChatDeskKeys {
  return { ...s, chatDesk: desk, chatLarge: desk === 'docked' ? s.chatLarge : desk === 'large' };
}

/** The size press of the box head: SMALL ⇄ LARGE. */
export function sizeDesk(s: ChatDeskKeys): ChatDeskKeys {
  return pickDesk(s, s.chatDesk === 'large' ? 'small' : 'large');
}

/** DOCK of the box head: the open box becomes the pane, so a pane that
 *  rested at its line opens at its height (R1870). */
export function dockDesk(s: ChatDeskKeys): ChatDeskKeys {
  return { ...pickDesk(s, 'docked'), chatStop: s.chatStop === 'line' ? 'pane' : s.chatStop };
}

/** UNDOCK of the pane head: the box floats open at the last of SMALL and
 *  LARGE. */
export function undockDesk(s: ChatDeskKeys): ChatDeskKeys {
  return { ...pickDesk(s, s.chatLarge ? 'large' : 'small'), chat: true };
}

// --- the desktop chat pane (card 15 of round 085, open point 5) ------------

// The three stops of the desktop chat pane are `CHAT_STOPS` of the
// settings: the chat line alone (no pane), the pane at its own height, and
// the whole dock column. The setting `chatStop` keeps the stop, and
// `chatPanePx` the height of the pane (R1870).

/** Where a release of the grip leaves the pane: the stop, and at `pane`
 *  the height the pane keeps. */
export interface PaneRelease {
  stop: ChatStop;
  px: number;
}

/**
 * The stop a drag of the grip ends on (R1870): the free height the finger
 * left, inside the column. A height in the line catch
 * (`chat_pane_line_catch_px`) goes to the chat line, and one within
 * `chat_pane_column_catch_px` of the column takes the whole column. In a
 * column too short for both catches the nearer end takes the release.
 * Up to R1870 every release went to the nearest of three fixed stops, so
 * a pane never kept a height of its own: a release at a middle height
 * sprang back to `chat_pane_px`, and a release past halfway to an end
 * took that end.
 */
export function releasePane(h: number, columnPx: number): PaneRelease {
  const col = Math.max(0, columnPx);
  const px = Math.round(Math.max(0, Math.min(col, h)));
  const nearLine = px <= LAYOUT_CHAT_PANE_LINE_CATCH_PX;
  const nearCol = px >= col - LAYOUT_CHAT_PANE_COLUMN_CATCH_PX;
  if (nearLine && nearCol) return { stop: px * 2 <= col ? 'line' : 'column', px };
  if (nearLine) return { stop: 'line', px };
  if (nearCol) return { stop: 'column', px };
  return { stop: 'pane', px };
}

// --- the telephone chat band (card 05 b of round 087) ----------------------

/** The lines the log of the chat band shows: `chat_band_lines`, or
 *  `chat_band_keyboard_lines` while the keyboard stands. The log scrolls
 *  inside the band for the rest. */
export function bandLines(keyboard: boolean): number {
  return keyboard ? CHAT_BAND_KEYBOARD_LINES : CHAT_BAND_LINES;
}

/**
 * The height of the keyboard over the bottom of the layout viewport, in
 * CSS px: what the visual viewport lost at its foot. A browser that shrinks
 * the layout viewport for the keyboard reports 0, and the band then stands
 * at the foot of the screen as always. A loss under `floorPx` is a toolbar
 * that moved, not a keyboard.
 */
export function keyboardInset(
  innerHeight: number, vv: { height: number; offsetTop: number } | null, floorPx = 80,
): number {
  if (!vv) return 0;
  const lost = innerHeight - vv.height - vv.offsetTop;
  return lost >= floorPx ? Math.round(lost) : 0;
}

// --- the chat line (card 16 of round 085) ----------------------------------

/** The newest live line that counted as unread, as the state keeps it. */
export interface ChatFresh {
  channel: ChatChannel;
  /** The sender as the log names it; '' for the world's own voice. */
  sender: string;
  /** The words, or the words of a quick line in the reader's language. */
  text: string;
  /** The sender's faction, or 0 on CLAN, which colours no sender (R242). */
  faction: number;
  /** A quick line (R1607): the line carries ◆. */
  quick: boolean;
  /** `performance.now()` when the line arrived. */
  atMs: number;
}

/** What the chat line shows. */
export type ChatLineView =
  | { kind: 'new'; fresh: ChatFresh; count: number }
  | { kind: 'count'; count: number; where: [ChatChannel, number][] }
  | { kind: 'read'; channel: ChatChannel }
  | { kind: 'wait'; count: number };

/** Is a fresh line still inside its life? */
export function freshLive(fresh: ChatFresh | null, nowMs: number): boolean {
  return fresh !== null && nowMs - fresh.atMs >= 0 && nowMs - fresh.atMs < CHAT_LINE_MS;
}

/**
 * The channels that hold unread lines, in the order of the game (WORLD,
 * FACTION, SQUAD, CLAN), each with its count. CLAN counts only for a
 * commander in a clan, by the rule of the channel tabs.
 */
export function unreadWhere(unread: ReadonlyMap<ChatChannel, number>, inClan: boolean): [ChatChannel, number][] {
  const out: [ChatChannel, number][] = [];
  for (const [, ch] of CHANNELS) {
    const n = unread.get(ch) ?? 0;
    if (n <= 0 || (ch === ChatChannel.CHAT_CHANNEL_CLAN && !inClan)) continue;
    out.push([ch, n]);
  }
  return out;
}

/**
 * What the chat line shows now. A new line shows for `chat_line_sec` with
 * its channel, its sender and its words; then the line reads CHAT with the
 * count and the channels that hold unread lines, or "all read". While a
 * siege warning stands, a new line only adds to the count, and the line
 * says that the lines wait. `count` is the figure a light count shows: 0
 * while the chat count switch of SOCIAL is off (R1759).
 */
export function chatLineView(o: {
  fresh: ChatFresh | null;
  nowMs: number;
  siege: boolean;
  unread: ReadonlyMap<ChatChannel, number>;
  inClan: boolean;
  channel: ChatChannel;
  countOn: boolean;
}): ChatLineView {
  const where = unreadWhere(o.unread, o.inClan);
  const total = where.reduce((n, [, c]) => n + c, 0);
  const count = o.countOn ? total : 0;
  if (o.siege) return { kind: 'wait', count };
  if (freshLive(o.fresh, o.nowMs)) return { kind: 'new', fresh: o.fresh!, count };
  if (total > 0) return { kind: 'count', count, where };
  return { kind: 'read', channel: o.channel };
}

/**
 * Does a new line take the peek line of a telephone from the advisor? The
 * two share one slot (card 16 of round 085, its risk rule): a SQUAD or a
 * CLAN line takes it for its life; a WORLD or a FACTION line takes it only
 * while no advisor line and no sea trials stand, and else adds to the
 * count on VIEW alone.
 */
export function freshTakesPeek(fresh: ChatFresh, advisor: boolean): boolean {
  if (!advisor) return true;
  return fresh.channel === ChatChannel.CHAT_CHANNEL_SQUAD || fresh.channel === ChatChannel.CHAT_CHANNEL_CLAN;
}

/**
 * The chat line a telephone stands in the peek slot, or null (R1799,
 * R2369). While the band is closed the chat rests as its chat line: every
 * form of the line, "all read" too, so ▾ minimizes the band to its line
 * and never to nothing. The CHAT tile of VIEW turns the chat off (`off`):
 * no line stands until the chat opens again. A new line takes the slot
 * from the advisor line by `freshTakesPeek`; every other form yields the
 * slot to the advisor line. The band, the welcome card and the open rows
 * of the sea trials (`held`, R1572, R2138) stand no line. A line that is
 * not null takes the whole slot.
 */
export function phoneChatLine(
  line: ChatLineView, o: { band: boolean; held: boolean; advisor: boolean; off: boolean },
): ChatLineView | null {
  if (o.band || o.held || o.off) return null;
  if (!o.advisor) return line;
  return line.kind === 'new' && freshTakesPeek(line.fresh, true) ? line : null;
}

/** The word of a channel, as its tab writes it. */
function channelWord(ch: ChatChannel): string {
  return CHANNELS.find(([, c]) => c === ch)?.[0] ?? '';
}

/** The channel a press on the chat line opens: the channel of the new
 *  line, else the first channel that holds unread lines, else the open
 *  one. */
export function lineChannel(v: ChatLineView, open: ChatChannel): ChatChannel {
  if (v.kind === 'new') return v.fresh.channel;
  if (v.kind === 'count' && v.where.length > 0) return v.where[0][0];
  return open;
}

/** The line icons of the chat, in the 24-unit box of the dock icons. */
const CHAT_ICON = {
  // A speech box: the chat.
  chat: '<path d="M4 5.5h16v10.5H10l-4.5 3.5V16H4z"/>',
  // A wheel: the quick wheel.
  quick: '<circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="3"/>'
    + '<path d="M6 6l3.9 3.9M18 6l-3.9 3.9M6 18l3.9-3.9M18 18l-3.9-3.9"/>',
  // A chevron down: close the band, or the pane to its line.
  down: '<path d="M6 9l6 6 6-6"/>',
  // A chevron up: the chat line opens the chat.
  up: '<path d="M6 15l6-6 6 6"/>',
} as const;

/** One line icon of the chat, `px` square, hidden from a screen reader. */
export function chatIcon(name: keyof typeof CHAT_ICON, px = 15): string {
  return lineIcon('cic', px, CHAT_ICON[name]);
}

/**
 * The inside of the chat line: the chat icon, the light count, the channel
 * word, then the sender in the owner ink and the words of a new line, or
 * the place of the unread lines. A press on the line opens the chat, so it
 * carries ▴ where the press opens something. `ink` is the CSS colour of a
 * faction; CLAN colours no sender.
 */
export function chatLineHtml(v: ChatLineView, ink: (faction: number) => string): string {
  const head = chatIcon('chat') + lightCount(v.kind === 'read' ? 0 : v.count);
  if (v.kind === 'new') {
    const f = v.fresh;
    const color = f.faction > 0 ? ` style="color:${ink(f.faction)}"` : '';
    const who = f.sender === '' ? '' : `<span class="w"${color}>${esc(f.sender)}</span>`;
    const words = f.quick ? `<span class="tx q">◆ ${esc(f.text)}</span>` : `<span class="tx">${esc(f.text)}</span>`;
    return head + `<span class="ch">${esc(channelWord(f.channel))}</span>` + who + words;
  }
  // The other three read CHAT and one sentence, and every one but the
  // wait of a siege carries ▴.
  const words = v.kind === 'count'
    ? S.chatline_unread({ where: v.where.map(([c, n]) => `${channelWord(c)} ${n}`).join(' · ') })
    : v.kind === 'read' ? S.chatline_read({ channel: channelWord(v.channel) }) : S.chatline_wait;
  return head + `<span class="ch">${esc(S.chat_head)}</span><span class="tx">${esc(words)}</span>`
    + (v.kind === 'wait' ? '' : chatIcon('up', 12));
}

/**
 * The inside of one channel tab (card 19 of round 085): the word, then one
 * light count while the tab holds unread lines and is not the open tab. No
 * gold, and no second count class (R1800). The tab is a tab of the kit's
 * tab strip (R2011), so the word and the count take its classes.
 */
export function channelTabHtml(word: string, unread: number, open: boolean): string {
  const count = open || !(unread > 0) ? '' : `<span class="ch-lc">${unread > 99 ? '99+' : unread}</span>`;
  return `<span class="ch-tl">${esc(word)}</span>${count}`;
}
